import React from 'react';
import PageEnvelope from '../elements/PageEnvelope';
import AlertModal from '../modals/AlertModal';
import { Server } from '../../server/server';
import { Navigate, NavLink } from 'react-router-dom';
import Portrait from '../elements/Portrait';
import { BsCaretRightFill, BsExclamationCircleFill, BsFillArrowLeftCircleFill, BsFillXCircleFill } from 'react-icons/bs';
import { BiSolidCrown } from 'react-icons/bi';
import MessageModal from '../modals/MessageModal';
import GroupChatModal from '../modals/GroupChatModal';
import UsersModal from '../modals/UsersModal';
import Modal from '../elements/Modal';
import { getBannerImage, getPortraitImage } from '../util/user';

interface ChatsPageState {
  navigate: string;
  alert: string;
  message: string;
  showFriends: boolean;
  selectFriends: any[];
  selectFriendCallback: Function;
  showGroup: boolean;
  showNew: boolean;
  chats: any[];
  loadingMore: boolean;
  noMoreChats: boolean;
  loading: boolean;
}

class ChatsPage extends React.Component<{}, ChatsPageState> {
  protected processing: boolean = false;

  constructor(props:{}) {
    super(props);
    this.state = {
      navigate: '',
      alert: '',
      message: '',
      showFriends: false,
      selectFriends: [],
      selectFriendCallback: null,
      showGroup: false,
      showNew: false,
      chats: [],
      loadingMore: false,
      noMoreChats: false,
      loading: false,
    }

    this.processing = true;

    this.onChatDatesUpdated = this.onChatDatesUpdated.bind(this);
    this.onChatStarted = this.onChatStarted.bind(this);
    this.onChatLeft = this.onChatLeft.bind(this);
  }

  componentDidMount(): void {
    if(Server.account.isLoggedIn()) 
      this.loadChats();

    Server.monitor.addEventListener('chat-dates-updated', this.onChatDatesUpdated);
    Server.monitor.addEventListener('chat-started', this.onChatStarted);
    Server.network.addEventListener('chat-left', this.onChatLeft);
  }

  componentWillUnmount(): void {
    Server.monitor.removeEventListener('chat-dates-updated', this.onChatDatesUpdated);
    Server.monitor.removeEventListener('chat-started', this.onChatStarted);
    Server.network.removeEventListener('chat-left', this.onChatLeft);
  }

  async loadChats() {
    this.processing = true;

    let userChatProfiles = [];
    let userChats = Server.user.getChats();
    for(let i = 0; i < userChats.length; i++) 
      if(userChats[i].type == 'direct') 
        userChatProfiles.push(userChats[i].user);

    await Server.public.loadProfiles(userChatProfiles);

    let cachedChats = await Server.chat.getDatesCache();

    let updating = false;
    if(cachedChats) {
      let profiles = this.getProfilesInChats(cachedChats);
      await Server.public.loadProfiles(profiles);

      await this.checkForNewUserChats(cachedChats);

      this.setState({chats: cachedChats});

      updating = cachedChats.length > 0;
      let chatsRefreshTime = window.sessionStorage.getItem(`ChatsRefresh`);
      if(chatsRefreshTime) {
        let refreshTime = parseInt(chatsRefreshTime);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) {
          this.processing = false;
          return;
        }
      }
    }

    let params:any = {direct: true, group: true};

    if(updating)
      params.after = cachedChats[0].date;

    this.setState({loading: true});

    // await Server.monitor.sleep(3000);
    let response = await Server.chat.getChatDates(params);

    if(!response.success) {
      this.setState({alert: response.message, loading: false});
      this.processing = false;
      return;
    }

    window.sessionStorage.setItem(`ChatsRefresh`, new Date().getTime().toString());
    
    let newChats = response.chats;

    if(newChats.length == 0 && updating) {
      this.processing = false;
      this.setState({loading: false});
      return;
    }

    let profiles = this.getProfilesInChats(newChats);
    await Server.public.loadProfiles(profiles);

    let chats = [];
    if(updating)
      chats = [...cachedChats];

    chats = this.addNewChatsToExistingChats(newChats, chats);

    await Server.chat.setDatesCache(chats, updating);

    await this.checkForNewUserChats(chats);

    if(!updating) {
      let noMoreChats = chats.length < 10;
      this.setState({noMoreChats: noMoreChats});
    }

    this.setState({chats, loading: false});

    this.processing = false;
  }

  async loadMoreChats() {
    this.setState({loadingMore: true});

    let params:any = {limit: 10};

    let chat = this.state.chats[this.state.chats.length-1];
    params.before = chat.date;

    let response = await Server.chat.getChatDates(params);

    if(!response.success) {
      this.setState({loadingMore: false, alert: response.message});
      return;
    }

    let newChats = response.chats;

    if(newChats.length == 0) {
      this.setState({loadingMore: false, noMoreChats: true});
      return;
    }

    let profiles = this.getProfilesInChats(newChats);
    await Server.public.loadProfiles(profiles);

    let chats = this.addNewChatsToExistingChats(newChats, this.state.chats);

    await Server.chat.setDatesCache(chats, true);

    let noMoreChats = chats.length < 10;

    this.setState({chats, noMoreChats, loadingMore: false});
  }

  getProfilesInChats(chats:any[]) {
    let profiles = [];
    for (let i = 0; i < chats.length; i++) {
      let chatId = chats[i].id;
      let chat = Server.user.getChat(chatId);
      if(chat && chat.user && profiles.indexOf(chat.user) == -1)
        profiles.push(chat.user);
    }
    return profiles;
  }
  
  addNewChatsToExistingChats(newChats:any[], existingChats:any[]) {
    let chats = [...existingChats];

    for (let newChat of newChats) {
      let existingIndex = chats.findIndex(chat => 
        chat.id === newChat.id
      );
      
      if (existingIndex !== -1) 
        chats[existingIndex] = newChat;
      else
        chats.push(newChat);
    }

    return chats;
  }

  async checkForNewUserChats(chats:any[]) {
    let userChats = Server.user.getChats();

    let needNewChats = false;
    for(let i = 0; i < chats.length; i++) 
      if(!userChats.find((c:any) => c.id == chats[i].id))
        needNewChats = true;

    if(needNewChats) {
      let response = await Server.chat.getChats();
      if(response.success) {
        let newUserChats = response.chats;
        Server.user.setChats(newUserChats);
        let newUserChatIds = newUserChats.map((c:any) => c.id);
        chats = chats.filter((chat:any) => newUserChatIds.includes(chat.id));
        Server.chat.setDatesCache(chats, true);
      }
    }
  }

  onChatStarted(event:any) {
    if(this.state.loadingMore || this.processing)
      return;
    window.sessionStorage.removeItem(`ChatsRefresh`);
    this.loadChats();
  }

  async onChatLeft(event:any) {
    if(this.state.loadingMore || this.processing)
      return;
    Server.user.leaveChat(event.chat);
    Server.monitor.clearChatLastRead(event.chat);
    Server.monitor.clearChatContent(event.chat);
    await Server.chat.clearDatesCache();
    this.loadChats();
  }

  onChatDatesUpdated(event:any) {
    if(this.state.loadingMore || this.processing)
      return;
    window.sessionStorage.removeItem(`ChatsRefresh`);
    this.loadChats();
  }

  getFriends(filter:string = '') {
    let userChats = Server.user.getChats();
    let userFriends = Server.user.getFriendsFromCache();
    
    let friends = [];
    for(let i = 0; i < userFriends.length; i++) {
      if(filter == 'direct' && userChats.find((c:any) => c.type == 'direct' && c.user == userFriends[i].id)) 
        continue;
      let profile = Server.public.getProfile(userFriends[i].id);
      if(!profile)
        continue;
      friends.push(profile);
    }

    friends.sort((a, b)=>{
      if(a.name > b.name)
        return 1;
      else if(a.name < b.name)
        return -1;
      return 0;
    })

    return friends;
  }

  formatDate(date: Date): string {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    // Format the date string
    let dateString: string;
    if (isToday) {
        dateString = "Today";
    } else {
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
        dateString = date.toLocaleDateString('en-US', options);
    }

    // Format the time string
    const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const timeString = date.toLocaleTimeString('en-US', options);

    return `${dateString}, ${timeString}`;
  }

  onCreateAccount() {
    sessionStorage.setItem('HomePageSignup', '1');
    this.setState({navigate: '/'});
  }

  onNewChat() {
    if(!Server.account.isLoggedIn()) {
      this.setState({alert: 'Please login to start a new chat with a friend or group.'});
      return;
    }

    this.setState({showNew: true});
  }

  onDirectChat() {
    this.setState({showNew: false});

    let friends = this.getFriends('direct');

    if(friends.length == 0) {
      this.setState({alert: 'You are already chatting with all of your friends.'});
      return;
    }

    this.setState({showFriends: true});
  }

  startDirectChat(id:string) {
    this.setState({showFriends: false});
    this.setState({message: 'Starting chat...'});

    setTimeout(async () => {
      let response = await Server.chat.startChat({
        type: 'direct',
        members: [id]
      });

      this.setState({message: ''});

      if(response.success) {
        let friend = Server.public.getProfile(id);
        Server.user.startChat({id: response.id, type: 'direct', user: id});
        Server.network.sendCommand('start-chat', {chat: response.id, type: 'direct', user: id});
        this.setState({navigate: `/chat/${friend.slug}`});
      }
      else
        this.setState({alert: response.message})
    }, 500);
  }

  onGroupChat() {
    this.setState({showNew: false, showGroup: true});
  }

  onRowClick(row:any) {
    if(row.type == 'club')
      sessionStorage.setItem('ClubPageTab', 'chat');
    this.setState({navigate: row.link})
  }

  renderNewChatModal() {
    return (
      <Modal title="New Chat" onClose={()=>this.setState({showNew: false})}>
        <div className="site-page-column">
          <div className="site-page-subtext" style={{margin: '5px 0px 5px 0px'}}>What type of chat do you want to start?</div>
          <button onClick={()=>this.onDirectChat()}>Chat with a friend</button>
          <button onClick={()=>this.onGroupChat()}>Chat with a group of friends</button>
        </div>        
      </Modal>
    )
  }

  render() {
    if(this.state.navigate != '')
      return <Navigate to={this.state.navigate} />

    let rows = [];

    let chats = JSON.parse(JSON.stringify(Server.user.getChats()));
   
    this.state.chats.forEach((chat:any) => {
      let chatIndex = chats.findIndex((c:any) => c.id === chat.id);
      if (chatIndex !== -1) 
        chats[chatIndex].date = chat.date;
    });

    chats.forEach((chat:any)=>{
      let name = '';
      let link = '';
      let portrait = null;
      let group = false;

      if(chat.type == 'direct') {
        let profile = Server.public.getProfile(chat.user);
        if(!profile)
          return;
        name = profile.name;
        link = '/chat/' + profile.slug;
        portrait = chat.user;
      }
      else {
        name = chat.name;
        link = '/chat/group/' + chat.id;
        group = true;
      }

      rows.push({
        type: chat.type,
        name,
        date: chat.date ? chat.date : '',
        link,
        portrait,
        group,
        host: false,
        notify: Server.monitor.isNewChatMessages(chat.id)
      });
    })

    rows.sort((a, b)=>{
      if(a.date > b.date)
        return -1;
      else if(a.date < b.date)
        return 1;

      if(a.name > b.name)
        return 1;
      else if(a.name < b.name)
        return -1;
      
      return 0;
    });

    let divs = [];

    rows.forEach((row:any, i:number)=>{
      let backgroundImage = 'unset';
      let backgroundSize = 'unset'; 
      let backgroundPosition = 'unset';

      if(row.type == 'club') {
        backgroundImage = `linear-gradient(to right, rgba(102, 40, 164), rgba(102, 40, 164, 0.8), rgba(0, 0, 0, 0)), url(${getBannerImage({banner: row.banner})})`; 
        backgroundSize = 'cover';
        backgroundPosition = 'center';
      }

      let description = '';
      if(row.date)
        description = this.formatDate(new Date(row.date));
      else
        description = '';

      divs.push(
        <div key={i} id="ChatPanel"className="site-page-panel" style={{cursor: 'pointer', padding: '5px 10px 5px 10px', backgroundImage, backgroundSize, backgroundPosition, textDecoration: 'none', borderRadius: '10px'}} onClick={()=>this.onRowClick(row)}>
          <div className="site-page-row">
            {row.group ?
              <img className="site-page-portrait" style={{width: '40px', height: '40px'}} src="portrait-group.png" />
            :
              <Portrait user={row.portrait} size={0.9} />
            }
            <div className="site-page-column" style={{rowGap: '0px'}}>
              <div>{row.name}{row.host == Server.user.getId() && <BiSolidCrown style={{color: 'yellow', transform: 'translateY(2px)', paddingLeft: '10px'}} />}</div>
              <div className="site-page-subtext">{description}</div>
            </div>
            {row.notify &&
              <div className="site-page-notify-circle">
                <BsExclamationCircleFill size="1.2em" />
              </div>
            }
            <div style={{fontSize: '1.4em', marginTop: '5px'}}><BsCaretRightFill /></div>
          </div>
        </div>
      )
    });

    if(divs.length == 0) {
      divs.push(
        <div className="site-page-panel" key={0}>You are not a member of any chats yet.</div>
      )
    }

    if(this.state.loading) {
      divs = [
        <div className="site-page-panel" key={0}>Loading chats...</div>
      ]
    }

    if(this.processing && this.state.chats.length == 0 && Server.account.isLoggedIn()) 
      divs = null;

    let friends = this.getFriends('direct');
    let friendIds = friends.map((f:any)=>f.id);

    return (
      <div className="site-page">
        <PageEnvelope width="500px">
          {/* {!Server.account.isLoggedIn() &&
            <div key={1} className="site-page-info-panel" style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{fontSize: '1.2em'}}><b>Be Part of the Community</b></div>
              <div style={{maxWidth: '500px', margin: 'auto'}}>Create a free account to engage in live chat with your friends.</div>
              <div style={{marginTop: '10px'}}>
                <button onClick={()=>this.onCreateAccount()}>Create Account</button>
              </div>
            </div>
          } */}
          <div className="site-page-column">
            <div className="site-page-header">
              <div className="site-page-row" style={{justifyContent: 'space-between'}}>
                <div>Chats</div>
                <div style={{cursor: 'pointer'}} onClick={()=>this.onNewChat()}>+New</div>
              </div>
            </div>
            {divs}
          </div>
          {this.state.showGroup && 
            <GroupChatModal 
              title="New Group Chat"
              onBack={()=>this.setState({showGroup: false, showNew: true})}
              onClose={()=>this.setState({showGroup: false})}
            />
          }
          {this.state.showFriends  && 
            <UsersModal 
              title="Select Friend"
              users={friendIds}
              onClick={(id:string)=>this.startDirectChat(id)}
              onBack={()=>this.setState({showFriends: false, showNew: true})}
              onClose={()=>this.setState({showFriends: false})}
            />
          }
          {this.state.showNew && this.renderNewChatModal()}
          <MessageModal message={this.state.message} />
          <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        </PageEnvelope>
      </div>
    );
  }
}

export default ChatsPage;
